'use strict';

/**
 * Read-view tab-bar decoration — render the GENERIC pict-section-recordset read view (RSP-RecordSet-Read) tab
 * strip with the app's shared TabBar LOOK (compact underline-active tabs + FontAwesome icons + horizontal-scroll
 * overflow) instead of the boxy `.psrs-tab` pills, so every master-list read view (Line Item, Material, Lab,
 * Sample, TestSpecificationSet, …) matches the bespoke workspaces (Project / User / Access / Contract …) that
 * already use `views/TabBar.js`. App-side, best-effort, NO node_module edits — same pattern as
 * Association-Chip-Decoration.js.
 *
 * SEAM: RecordSet-Read renders each tab from `tab.TabTemplate` (an HTML string built in `_structureTabs`) joined
 * into `<div id="PRSP-Read-Tab-Nav">` (RecordSet-Read.js renderRead). `setTab` toggles `.is-active` on the
 * elements matched by `document.querySelectorAll('.psrs-tab')` + the id `PSRS-TabNav-<Hash>`. So we wrap
 * `_structureTabs` and rewrite ONLY each tab's `TabTemplate` into a button that KEEPS `class="psrs-tab"` and
 * `id="PSRS-TabNav-<Hash>"` (so `setTab` needs NO patch) and adds an icon + label span. The underline look is
 * applied by the CSS block injected in PlatformConfiguration-Application-Web.js (scoped under the app container
 * id so it outranks the `.psrs-split-tabnav` pill rules by specificity). Everything else on the tab
 * (`Template` / `Hash` / `render` / `Type`) is left untouched, so the body + render pipeline is unchanged.
 *
 * MIGRATION: this is a PROTOTYPE to prove the look on the generic read view before lifting it upstream into
 * pict-section-recordset itself (RecordSet-Read._structureTabs emitting `.hltab` markup + carrying TabBar's CSS).
 * When that lands: delete this module + its wiring + the injected CSS block; the markup here mirrors TabBar.js's
 * `.hltab` item (icon span + `.hltab-label`) precisely to make that port a drop-in. See TabBar.js.
 */

// Per-tab-TYPE fallback icon (RecordSetReadTabs entries carry a Type; FullRecord/Record are the record itself,
// Association = a join editor, View = a filtered child list). A curated per-entity icon could later be threaded
// through MasterList-Builder.buildReadTabs, but the type-level default already gives every tab a meaningful glyph.
const ICON_BY_TYPE =
{
	FullRecord: 'fas fa-file-lines',
	Record: 'fas fa-file-lines',
	Association: 'fas fa-link',
	View: 'fas fa-list',
	Manifest: 'fas fa-file-lines',
	AttachedRecord: 'fas fa-paperclip',
};
const ICON_DEFAULT = 'fas fa-folder';

function _escapeAttr(pValue)
{
	return String(pValue == null ? '' : pValue).replace(/[&<>"']/g, (pChar) =>
		({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[pChar]));
}

/**
 * Build the shared-TabBar button markup for one tab, preserving the `.psrs-tab` + `PSRS-TabNav-<Hash>` hooks the
 * module's `setTab` drives. `hltab has-icon` is carried as a forward marker for the eventual PSRS migration; the
 * active styling is applied by the app-side CSS block via the `.psrs-tab` / `.is-active` classes.
 * @param {{Hash:String, Title:String, Type?:String, Icon?:String}} pTab
 * @returns {String}
 */
function readTabTemplate(pTab)
{
	const tmpHash = _escapeAttr(pTab.Hash);
	const tmpTitle = _escapeAttr(pTab.Title);
	const tmpIcon = _escapeAttr(pTab.Icon || ICON_BY_TYPE[pTab.Type] || ICON_DEFAULT);
	// navigateTab (not setTab) so the read view's tab is a stable, deep-linkable route
	// (#/PSRS/<RecordSet>/View/<GUID>/<Tab>) — added upstream in pict-section-recordset 1.25.13.
	return `<button type="button" class="psrs-tab hltab has-icon" id="PSRS-TabNav-${ tmpHash }" title="${ tmpTitle }" onclick="_Pict.views['RSP-RecordSet-Read'].navigateTab('${ tmpHash }')"><span class="icon"><i class="${ tmpIcon }"></i></span><span class="hltab-label">${ tmpTitle }</span></button>`;
}

/**
 * Wrap the read view's `_structureTabs` so every tab renders with the shared TabBar look. Idempotent; best-effort
 * per tab (a rewrite failure keeps that tab's original TabTemplate, so a bad tab never blanks the whole strip).
 * @param {any} pReadView - the `RSP-RecordSet-Read` view instance.
 * @returns {Boolean} true when the wrap is in place after this call.
 */
function patchReadViewTabBar(pReadView)
{
	if (!pReadView || (typeof pReadView._structureTabs !== 'function')) { return false; }
	if (pReadView.__readViewTabBarPatched) { return true; }
	pReadView.__readViewTabBarPatched = true;
	const fOriginal = pReadView._structureTabs.bind(pReadView);
	pReadView._structureTabs = async function (pConfig, pRecord)
	{
		const tmpTabs = await fOriginal(pConfig, pRecord);
		if (Array.isArray(tmpTabs))
		{
			tmpTabs.forEach((pTab) =>
			{
				try { if (pTab && pTab.Hash) { pTab.TabTemplate = readTabTemplate(pTab); } }
				catch (pError) { /* keep this tab's original TabTemplate — never break the tab strip */ }
			});
		}
		return tmpTabs;
	};
	return true;
}

/**
 * Give the read view's tab strip the shared TabBar "More ▾" overflow behaviour (the `hltabs--menu` strategy the
 * bespoke workspaces use): tabs that don't fit collapse into a right-aligned dropdown instead of scrolling off
 * unreachably. After each `setTab` (which the module also calls once on first paint), we tag `#PRSP-Read-Tab-Nav`
 * as an `.hltabs--menu` bar and let `TabBar.layout` partition it — on a rAF so widths are measured post-layout.
 * Idempotent; best-effort. Requires `TabBar.TABBAR_CSS` to be injected (for `.hltabs-more` / `.is-overflow`).
 * @param {any} pReadView - the `RSP-RecordSet-Read` view instance.
 * @param {{layout:Function}} pLibTabBar - the `views/TabBar.js` module.
 * @returns {Boolean} true when the wrap is in place after this call.
 */
function wrapReadViewMenuOverflow(pReadView, pLibTabBar)
{
	if (!pReadView || !pLibTabBar || (typeof pReadView.setTab !== 'function') || (typeof pLibTabBar.layout !== 'function')) { return false; }
	if (pReadView.__readViewMenuOverflowWrapped) { return true; }
	pReadView.__readViewMenuOverflowWrapped = true;
	const fRelayout = () =>
	{
		try
		{
			if (typeof document === 'undefined') { return; }
			const tmpNav = document.getElementById('PRSP-Read-Tab-Nav');
			if (!tmpNav) { return; }
			tmpNav.classList.add('hltabs', 'hltabs--menu');
			// Measure after the browser has laid the row out (offsetLeft/clientWidth) rather than mid-mutation.
			if ((typeof window !== 'undefined') && window.requestAnimationFrame)
			{
				window.requestAnimationFrame(() => { try { pLibTabBar.layout(document); } catch (pInner) { /* best-effort */ } });
			}
			else { pLibTabBar.layout(document); }
		}
		catch (pError) { /* overflow handling is best-effort — never break the tab bar */ }
	};
	const fOriginalSetTab = pReadView.setTab.bind(pReadView);
	pReadView.setTab = async function (pTab)
	{
		const tmpResult = await fOriginalSetTab(pTab);
		fRelayout();
		return tmpResult;
	};
	return true;
}

module.exports = { patchReadViewTabBar, wrapReadViewMenuOverflow, readTabTemplate, ICON_BY_TYPE };
